import { Form, FormInstance, Select } from 'antd';
import { requiredRule } from 'const/formRules';
import { useAdemeVulVehicles } from 'hooks/dossiers.hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ADEME_VEHICLE_NOT_FOUND_VALUE,
  AdemeVulVehicle,
  NouveauDossierConcessionnaireData,
} from 'types/Concessionnaire';

interface AdemeVulVehicleFieldsProps {
  form: FormInstance<NouveauDossierConcessionnaireData>;
}

function getDistinctValues(
  vehicles: AdemeVulVehicle[] | undefined,
  key: keyof AdemeVulVehicle,
) {
  return Array.from(new Set((vehicles ?? []).map((vehicle) => vehicle[key])))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'fr'));
}

function toSelectOptions(values: string[], notFoundLabel: string) {
  return [
    {
      label: notFoundLabel,
      value: ADEME_VEHICLE_NOT_FOUND_VALUE,
    },
    ...values.map((value) => ({ label: value, value })),
  ];
}

function isRealAdemeValue(value?: string) {
  return !!value && value !== ADEME_VEHICLE_NOT_FOUND_VALUE;
}

export default function AdemeVulVehicleFields({
  form,
}: AdemeVulVehicleFieldsProps) {
  const { t } = useTranslation();
  const { data: vehicles, isLoading } = useAdemeVulVehicles();
  const marque = Form.useWatch('ademeMarque', form);
  const modele = Form.useWatch('ademeModele', form);
  const notFoundLabel = t('nouveau_formulaire:ademeVulVehicle:not_found');

  const marqueOptions = useMemo(
    () => toSelectOptions(getDistinctValues(vehicles, 'marque'), notFoundLabel),
    [notFoundLabel, vehicles],
  );

  const modeleOptions = useMemo(() => {
    const modeles = getDistinctValues(
      vehicles?.filter((vehicle) => vehicle.marque === marque),
      'modele',
    );

    return toSelectOptions(modeles, notFoundLabel);
  }, [marque, notFoundLabel, vehicles]);

  const typeVarianteOptions = useMemo(() => {
    const typeVariantes = getDistinctValues(
      vehicles?.filter(
        (vehicle) => vehicle.marque === marque && vehicle.modele === modele,
      ),
      'typeVariante',
    );

    return toSelectOptions(typeVariantes, notFoundLabel);
  }, [marque, modele, notFoundLabel, vehicles]);

  const showModele = isRealAdemeValue(marque);
  const showTypeVariante = showModele && isRealAdemeValue(modele);

  const handleMarqueChange = (value: string) => {
    form.setFieldsValue({
      ademeModele: undefined,
      ademeTypeVariante: undefined,
      ademeVehicleNotFound: value === ADEME_VEHICLE_NOT_FOUND_VALUE,
    });
  };

  const handleModeleChange = (value: string) => {
    form.setFieldsValue({
      ademeTypeVariante: undefined,
      ademeVehicleNotFound: value === ADEME_VEHICLE_NOT_FOUND_VALUE,
    });
  };

  const handleTypeVarianteChange = (value: string) => {
    form.setFieldsValue({
      ademeVehicleNotFound: value === ADEME_VEHICLE_NOT_FOUND_VALUE,
    });
  };

  return (
    <div className="space-y-4 mb-4">
      <p className="-mb-2 text-base text-bleu-hellio">
        {t('nouveau_formulaire:ademeVulVehicle:section_title')}
      </p>

      <Form.Item<NouveauDossierConcessionnaireData>
        label={t('nouveau_formulaire:ademeVulVehicle:marque:label')}
        name="ademeMarque"
        required
        rules={[requiredRule]}
      >
        <Select
          showSearch
          optionFilterProp="label"
          loading={isLoading}
          options={marqueOptions}
          placeholder={t(
            'nouveau_formulaire:ademeVulVehicle:marque:placeholder',
          )}
          onChange={handleMarqueChange}
        />
      </Form.Item>

      {showModele && (
        <Form.Item<NouveauDossierConcessionnaireData>
          label={t('nouveau_formulaire:ademeVulVehicle:modele:label')}
          name="ademeModele"
          required
          rules={[requiredRule]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            loading={isLoading}
            options={modeleOptions}
            placeholder={t(
              'nouveau_formulaire:ademeVulVehicle:modele:placeholder',
            )}
            onChange={handleModeleChange}
          />
        </Form.Item>
      )}

      {showTypeVariante && (
        <Form.Item<NouveauDossierConcessionnaireData>
          label={t('nouveau_formulaire:ademeVulVehicle:type_variante:label')}
          name="ademeTypeVariante"
          required
          rules={[requiredRule]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            loading={isLoading}
            options={typeVarianteOptions}
            placeholder={t(
              'nouveau_formulaire:ademeVulVehicle:type_variante:placeholder',
            )}
            onChange={handleTypeVarianteChange}
          />
        </Form.Item>
      )}
    </div>
  );
}
